/**
 * LiteratureBlock — renders a `literature` content block as a lightweight citation list.
 *
 * Server-emitted only (the assistant never fabricates citations). Mirrors the
 * EvidenceRefsBlock / TestResultsBlock style: compact antd List, `--mg-*` tokens only,
 * and a hard guard on the items array so a malformed block can never throw and crash the
 * message thread (BlockBoundary in XoeBlockRenderer is the outer safety net).
 *
 * Each item renders as:  Author et al. Journal. Year. PMID: {n}
 * with links: PubMed (url ?? https://pubmed.ncbi.nlm.nih.gov/{pmid}/),
 *             DOI (https://doi.org/{doi}),
 *             Europe PMC (https://europepmc.org/article/MED/{pmid}).
 */

import React from 'react';
import { List, Typography, Space } from 'antd';

import type { XoeLiteratureRef } from '../../../types/xoe/XoeTypes';
import type { LiteratureBlockProps } from '../../../types/xoe/XoeUiTypes';

const { Text, Link } = Typography;

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** "Smith et al." from an authors array; falls back gracefully when empty/undefined. */
function formatAuthors(authors: string[] | undefined): string | null {
	const list = authors ?? [];
	if (list.length === 0) return null;
	if (list.length === 1) return list[0];
	return `${list[0]} et al.`;
}

/** "Author et al. Journal. Year." citation line, skipping any missing segments. */
function formatCitation(item: XoeLiteratureRef): string {
	const parts: string[] = [];
	const authors = formatAuthors(item.authors);
	if (authors) parts.push(authors);
	if (item.journal) parts.push(item.journal);
	if (item.year != null && `${item.year}`.length > 0) parts.push(String(item.year));
	return parts.join('. ');
}

function pubmedUrl(item: XoeLiteratureRef): string | null {
	if (item.url) return item.url;
	if (item.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${item.pmid}/`;
	return null;
}

// ── Public block component ────────────────────────────────────────────────────

const LiteratureBlock: React.FC<LiteratureBlockProps> = ({ items }) => {
	// `items` may be undefined on a malformed backend block — guard so the render can't throw.
	const safeItems = items ?? [];

	if (safeItems.length === 0) {
		return (
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
				No literature to display.
			</Text>
		);
	}

	return (
		<List
			size="small"
			split={false}
			dataSource={safeItems}
			renderItem={(item, idx) => {
				const citation = formatCitation(item);
				const pubmed = pubmedUrl(item);
				return (
					<List.Item key={item.pmid ?? item.doi ?? idx} style={{ padding: 'var(--mg-sp-2) 0' }}>
						<div style={{ width: '100%', minWidth: 0 }}>
							<Text style={{ fontSize: 'var(--mg-fs-md)', fontWeight: 600, display: 'block' }}>
								{item.title}
							</Text>
							{citation && (
								<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)', display: 'block' }}>
									{citation}
									{item.pmid ? ` · PMID: ${item.pmid}` : ''}
								</Text>
							)}
							<Space size={12} wrap style={{ marginTop: 'var(--mg-sp-1)' }}>
								{pubmed && (
									<Link
										href={pubmed}
										target="_blank"
										rel="noopener noreferrer"
										style={{ fontSize: 'var(--mg-fs-xs)' }}
									>
										PubMed
									</Link>
								)}
								{item.doi && (
									<Link
										href={`https://doi.org/${item.doi}`}
										target="_blank"
										rel="noopener noreferrer"
										style={{ fontSize: 'var(--mg-fs-xs)' }}
									>
										DOI
									</Link>
								)}
								{item.pmid && (
									<Link
										href={`https://europepmc.org/article/MED/${item.pmid}`}
										target="_blank"
										rel="noopener noreferrer"
										style={{ fontSize: 'var(--mg-fs-xs)' }}
									>
										Europe PMC
									</Link>
								)}
							</Space>
						</div>
					</List.Item>
				);
			}}
		/>
	);
};

export default LiteratureBlock;
